import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { integrationService, masterService } from '../../services/api';
import { Button, Modal, Badge, Skeleton, EmptyState, Toast, Input, Tab } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';
import { SYNC_LOG_STATUS_COLORS } from '../../constants';

const TABS = [
  { key: 'status', label: 'Setup Status' },
  { key: 'mappings', label: 'Channel Mappings' },
  { key: 'logs', label: 'Sync Logs' },
];

const IntegrationsPage = () => {
  const { dark } = useTheme();
  const [tab, setTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [retailOUs, setRetailOUs] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [logsPage, setLogsPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ platform: 'META', externalId: '', retailOU: '', leadSource: '' });

  const PAGE_SIZE = 25;

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await integrationService.getStatus();
      setStatus(data.data || data);
    } catch { setToast({ msg: 'Error loading integration status', type: 'error' }); }
  }, []);

  const fetchMappings = useCallback(async () => {
    try {
      const { data } = await integrationService.getMappings();
      setMappings(data.data || []);
    } catch { }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await integrationService.getLogs({ page: logsPage, limit: PAGE_SIZE });
      setLogs(data.data || []);
      setLogsTotal(data.total || 0);
    } catch { }
  }, [logsPage]);

  useEffect(() => {
    masterService.getRetailOUs().then(({ data }) => setRetailOUs(data.data || [])).catch(() => {});
    masterService.getLeadSources().then(({ data }) => setLeadSources(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = tab === 'status' ? fetchStatus() : tab === 'mappings' ? fetchMappings() : fetchLogs();
    p.finally(() => setLoading(false));
  }, [tab, fetchStatus, fetchMappings, fetchLogs]);

  const openCreate = () => { setEditMapping(null); setForm({ platform: 'META', externalId: '', retailOU: '', leadSource: '' }); setFormOpen(true); };
  const openEdit = (m) => { setEditMapping(m); setForm({ platform: m.platform, externalId: m.externalId, retailOU: m.retailOU?._id || m.retailOU || '', leadSource: m.leadSource?._id || m.leadSource || '' }); setFormOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMapping) await integrationService.updateMapping(editMapping._id, form);
      else await integrationService.createMapping(form);
      setToast({ msg: editMapping ? 'Mapping updated' : 'Mapping created', type: 'success' });
      setFormOpen(false);
      fetchMappings();
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Error', type: 'error' }); }
  };

  const handleDelete = async () => {
    try {
      await integrationService.deleteMapping(deleteId);
      setToast({ msg: 'Mapping deleted', type: 'success' });
      setDeleteId(null);
      fetchMappings();
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Error', type: 'error' }); }
  };

  const f = (field) => ({ value: form[field], onChange: e => setForm({ ...form, [field]: e.target.value }) });
  const card = { background: dark ? '#0F172A' : '#fff', border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, borderRadius: 16, overflow: 'hidden' };

  const StatusCheck = ({ label, ok }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, background: dark ? '#1E293B' : '#F8FAFC', border: `1px solid ${ok ? '#10B981' : (dark ? '#334155' : '#E2E8F0')}`, marginBottom: 8 }}>
      {ok ? <CheckCircle size={18} color="#10B981" /> : <XCircle size={18} color="#EF4444" />}
      <span style={{ fontSize: 13, fontWeight: 500, color: dark ? '#F1F5F9' : '#0F172A' }}>{label}</span>
      <Badge color={ok ? '#10B981' : '#EF4444'} style={{ marginLeft: 'auto' }}>{ok ? 'Configured' : 'Not Set'}</Badge>
    </div>
  );

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Integrations</h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>Meta Lead Ads & Google Ads auto-sync configuration</p>
        </div>
        {tab === 'mappings' && <Button icon={Plus} onClick={openCreate}>Add Mapping</Button>}
        {tab === 'logs' && <Button variant="outline" icon={RefreshCw} onClick={fetchLogs}>Refresh</Button>}
      </div>

      <div style={card}>
        <div style={{ padding: '16px 20px 0', borderBottom: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}` }}>
          <Tab tabs={TABS} active={tab} onChange={setTab} />
        </div>

        <div style={{ padding: 24 }}>
          {loading ? <Skeleton rows={4} cols={3} /> : tab === 'status' && status ? (
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: dark ? '#94A3B8' : '#64748B', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 12 }}>Meta (Facebook & Instagram) Lead Ads</h3>
              <StatusCheck label="META_APP_SECRET" ok={status.meta?.appSecret} />
              <StatusCheck label="META_PAGE_ACCESS_TOKEN" ok={status.meta?.pageAccessToken} />
              <StatusCheck label="META_WEBHOOK_VERIFY_TOKEN" ok={status.meta?.webhookVerifyToken} />
              <StatusCheck label="META_APP_ID" ok={status.meta?.appId} />

              <h3 style={{ fontSize: 14, fontWeight: 700, color: dark ? '#94A3B8' : '#64748B', letterSpacing: '0.05em', textTransform: 'uppercase', margin: '24px 0 12px' }}>Google Ads Lead Form</h3>
              <StatusCheck label="GOOGLE_ADS_WEBHOOK_KEY" ok={status.google?.webhookKey} />

              <div style={{ marginTop: 20, padding: 14, borderRadius: 10, background: dark ? '#1E3A5F' : '#EFF6FF', border: `1px solid ${dark ? '#2563EB' : '#BFDBFE'}`, fontSize: 12, color: dark ? '#93C5FD' : '#1D4ED8', lineHeight: 1.6 }}>
                <strong>Setup note:</strong> Configure environment variables in your server's <code>.env</code> file. See <code>.env.example</code> for the full list of required keys. Meta webhooks must be registered through the Meta Developer Console; Google Ads webhooks are configured in the Lead Form asset settings.
              </div>
            </div>
          ) : tab === 'mappings' && (
            <div style={{ overflowX: 'auto' }}>
              {mappings.length === 0 ? (
                <EmptyState title="No channel mappings configured" description="Add a mapping to route incoming ad leads to the correct Retail OU." />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                    <tr>
                      <th style={thStyle(dark)}>Platform</th>
                      <th style={thStyle(dark)}>External ID</th>
                      <th style={thStyle(dark)}>Retail OU</th>
                      <th style={thStyle(dark)}>Lead Source Override</th>
                      <th style={thStyle(dark)}>Status</th>
                      <th style={thStyle(dark)}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map(m => (
                      <tr key={m._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={tdStyle(dark)}><Badge color={m.platform === 'META' ? '#3B82F6' : '#F59E0B'}>{m.platform}</Badge></td>
                        <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>{m.externalId}</td>
                        <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{m.retailOU?.name || '—'}</td>
                        <td style={tdStyle(dark)}>{m.leadSource?.name || <span style={{ color: dark ? '#475569' : '#94A3B8', fontSize: 11 }}>Default for platform</span>}</td>
                        <td style={tdStyle(dark)}><Badge color={m.isActive ? '#10B981' : '#EF4444'}>{m.isActive ? 'Active' : 'Inactive'}</Badge></td>
                        <td style={tdStyle(dark)}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button style={iconBtnStyle(dark)} onClick={() => openEdit(m)}><Pencil size={14} /></button>
                            <button style={{ ...iconBtnStyle(dark), color: '#EF4444' }} onClick={() => setDeleteId(m._id)}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === 'logs' && (
            <div style={{ overflowX: 'auto' }}>
              {logs.length === 0 ? (
                <EmptyState title="No sync logs yet" description="Logs will appear here after your first lead sync from Meta or Google Ads." />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                    <tr>
                      <th style={thStyle(dark)}>Time</th>
                      <th style={thStyle(dark)}>Platform</th>
                      <th style={thStyle(dark)}>External Lead ID</th>
                      <th style={thStyle(dark)}>Status</th>
                      <th style={thStyle(dark)}>Lead Created</th>
                      <th style={thStyle(dark)}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ ...tdStyle(dark), fontSize: 11 }}>{new Date(log.createdAt).toLocaleString()}</td>
                        <td style={tdStyle(dark)}><Badge color={log.platform === 'META' ? '#3B82F6' : '#F59E0B'}>{log.platform}</Badge></td>
                        <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>{log.externalLeadId}</td>
                        <td style={tdStyle(dark)}><Badge color={SYNC_LOG_STATUS_COLORS[log.status] || '#6B7280'}>{log.status}</Badge></td>
                        <td style={tdStyle(dark)}>{log.lead?.leadId || '—'}</td>
                        <td style={{ ...tdStyle(dark), maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#EF4444', fontSize: 11 }}>{log.errorMessage || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {Math.ceil(logsTotal / PAGE_SIZE) > 1 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <Button size="sm" variant="outline" disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}>Previous</Button>
                  <Button size="sm" variant="outline" disabled={logsPage >= Math.ceil(logsTotal / PAGE_SIZE)} onClick={() => setLogsPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editMapping ? 'Edit Mapping' : 'Add Channel Mapping'} size="md">
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', display: 'block', marginBottom: 6 }}>Platform</label>
            <select {...f('platform')} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#fff', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none' }}>
              <option value="META">META (Facebook / Instagram)</option>
              <option value="GOOGLE">GOOGLE (Google Ads)</option>
            </select>
          </div>
          <Input label="External ID *" {...f('externalId')} placeholder={form.platform === 'META' ? 'Facebook Page ID' : 'Google Ads Campaign ID'} />
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', display: 'block', marginBottom: 6 }}>Retail OU *</label>
            <select {...f('retailOU')} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#fff', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none' }}>
              <option value="">Select Retail OU</option>
              {retailOUs.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', display: 'block', marginBottom: 6 }}>Lead Source Override (optional)</label>
            <select {...f('leadSource')} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#fff', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none' }}>
              <option value="">Use platform default</option>
              {leadSources.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <Button variant="outline" type="button" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit">{editMapping ? 'Save Changes' : 'Create Mapping'}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="Confirm Delete" size="sm">
        <p style={{ fontSize: 14, color: dark ? '#CBD5E1' : '#334155', margin: '0 0 20px' }}>Delete this channel mapping?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
};

export default IntegrationsPage;
